/**
 * Communications2 — the /phone v2 workspace.
 *
 *   /phone?v=2  ← this file
 *   /phone      ← the current v1 (Communications.jsx)
 *
 * Structure (see PART 2 of the rebuild spec):
 *   ┌──────────────────────────────────────────────────────┐
 *   │ CommsHeader (navy)                                    │
 *   ├────────┬─────────────────────────────────────────────┤
 *   │        │ Tab row: Inbox · Contacts · Text Blast · Fax│
 *   │ Left   ├─────────────────────────────────────────────┤
 *   │ Rail   │                                              │
 *   │ (gold  │  Tab content                                 │
 *   │  bar   │                                              │
 *   │  + …)  │                                              │
 *   └────────┴─────────────────────────────────────────────┘
 *
 * Feature flag: URL query param `?v=2`. No storage. The "use the old
 * page" pill in the header removes the param and reloads v1. Users are
 * one click from the escape hatch at all times.
 *
 * Per PART 8: no localStorage / no sessionStorage. State that has to
 * survive a reload lives in the URL (tab, filter, selected key,
 * hidden-only view). The junk-hide set is per-tab-session only — this
 * is called out explicitly in the report.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import CommsHeader from "./comms/CommsHeader";
import LeftRail from "./comms/LeftRail";
import InboxTab from "./comms/InboxTab";
import CallLogTab from "./comms/CallLogTab";
import ContactsTab from "./comms/ContactsTab";
import TextBlastTab from "./comms/TextBlastTab";
import FaxTab from "./comms/FaxTab";
import DirectoryTab from "../components/phone/DirectoryTab";
import NewTextComposerModal from "./comms/NewTextComposerModal";
import { useInboxRollup, last10 } from "./comms/useInboxRollup";
import { useCall } from "../components/layout/ActiveCallOverlay";
import {
  smsAPI, voicemailAPI, callsAPI, twilioAPI, adminAPI, contactsAPI, speedDialAPI,
} from "../services/api";
import { formatPhoneNumber, toErrorMessage, cn } from "../lib/utils";
import { NAVY, NAVY_HAIRLINE, GOLD } from "./comms/tokens";
import { useAuth } from "../context/AuthContext";

// Iter 506 · full-app contact spine — retire the confusing "Inbox"
// label. Users kept treating the Needs-a-response filter as if it were
// a mail-style inbox they had to empty. Split into 3 explicit surfaces:
//   * Call log    — complete activity feed, permanent, filterable
//   * Needs cb    — work queue, emptiable (calls/texts needing a reply)
//   * Voicemail   — work queue, emptiable
// Then keep Texts as the SMS thread view, add a Dialer surface, and
// leave Contacts + Fax untouched.
// Iter 508j (Dawn 2026-02-25) — 6-tab consolidation.
//   Killed: Dialer (left rail has the dial pad on every tab).
//   Killed: Voicemail tab (folded into Inbox as a filter chip).
//   Killed: Needs callback tab (Inbox's DEFAULT filter is now
//           "needs response" — same workflow, one fewer tab hop).
//   Kept: Inbox (work queue), Calls (pure log), Texts (SMS threads),
//         Contacts, Text Blast, Fax.
const TABS = [
  { k: "inbox",       label: "Inbox",        mode: "inbox", lockedFilter: null },
  { k: "calls",       label: "Calls",        mode: "calllog" },
  { k: "texts",       label: "Texts",        mode: "inbox", lockedFilter: "texts" },
  { k: "contacts",    label: "Contacts",     mode: "contacts" },
  { k: "directory",   label: "Directory",    mode: "directory" },
  { k: "text-blast",  label: "Text Blast",   mode: "textblast" },
  { k: "fax",         label: "Fax",          mode: "fax" },
];

// Legacy tab-key redirect. Old bookmarks pointed at explicit
// `?tab=needs`, `?tab=voicemail`, `?tab=dialer` tabs that no longer
// exist as first-class surfaces. Reroute them to Inbox with the
// appropriate filter chip so muscle memory keeps working.
const LEGACY_TAB_REDIRECT = {
  needs:     { tab: "inbox", filter: "needs" },
  voicemail: { tab: "inbox", filter: "vm" },
  dialer:    { tab: "inbox", filter: null },
};

// Normalize a phone-ish string to +E.164. Used for dialing / sending.
function e164(raw) {
  const d = String(raw || "").replace(/\D/g, "");
  if (!d) return "";
  if (d.length === 10) return `+1${d}`;
  if (d.length === 11 && d.startsWith("1")) return `+${d}`;
  return `+${d}`;
}

export default function Communications2({ embedded = false, onCounts }) {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const call = useCall() || {};
  const currentUser = useAuth().user || (() => { try { return JSON.parse(localStorage.getItem("user") || "{}"); } catch { return {}; } })();
  const isAdmin = ["super_admin", "admin", "owner", "global_operator"].includes(currentUser?.role);

  /* ─── URL-owned state (survives reload) ────────────────────── */
  const activeTab = TABS.find(t => t.k === params.get("tab"))?.k || "inbox";
  const setTab = (t) => setParams(p => { const n = new URLSearchParams(p); n.set("tab", t); n.delete("filter"); return n; }, { replace: true });
  const filter = params.get("filter") || "needs"; // "Needs a response" is the DEFAULT
  const setFilter = (f) => setParams(p => { const n = new URLSearchParams(p); n.set("filter", f); return n; }, { replace: true });

  // Iter 508j · legacy-tab redirect. Old bookmarks like ?tab=needs,
  // ?tab=voicemail, ?tab=dialer point at surfaces that no longer exist
  // as first-class tabs. Reroute them to Inbox with the appropriate
  // filter chip so muscle memory keeps working.
  useEffect(() => {
    const tabParam = params.get("tab");
    if (tabParam && LEGACY_TAB_REDIRECT[tabParam]) {
      const target = LEGACY_TAB_REDIRECT[tabParam];
      setParams(p => {
        const n = new URLSearchParams(p);
        n.set("tab", target.tab);
        if (target.filter) n.set("filter", target.filter);
        else n.delete("filter");
        return n;
      }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.get("tab")]);
  const openKey = params.get("who") || null;
  const setOpenKey = (k) => setParams(p => {
    const n = new URLSearchParams(p);
    if (k) n.set("who", k); else n.delete("who");
    return n;
  }, { replace: true });
  // Iter 489 — atomic multi-field URL update. Needed because
  // react-router's setSearchParams(functional) does NOT chain
  // functional updates across sequential calls in the same tick —
  // each closure reads the ORIGINAL params, so only the last call's
  // mutation survives. This helper lets `doText`/`doContactText`/etc.
  // change tab+filter+who in one go without losing fields.
  const setParamsBulk = (patch) => setParams(p => {
    const n = new URLSearchParams(p);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null || v === undefined) n.delete(k);
      else n.set(k, String(v));
    }
    return n;
  }, { replace: true });

  /* ─── Companies + outbound line ─────────────────────────────── */
  const [companies, setCompanies] = useState([]);
  const [fromNumber, setFromNumber] = useState("");

  /* ─── "New text" composer modal — opens the recipient picker so
        the gold button actually does something on the texts tab. */
  const [newTextOpen, setNewTextOpen] = useState(false);
  const openNewText = () => setNewTextOpen(true);
  const closeNewText = () => setNewTextOpen(false);
  const handleNewTextPick = (k, c) => {
    // Same routing rule as doContactText — but we always land on the
    // texts tab (that's what the user asked for by clicking "New
    // text") so filter is fixed to 'texts'.
    setParamsBulk({ tab: "texts", filter: "texts", who: k });
    const label = c ? `${c.first_name || ""} ${c.last_name || ""}`.trim() || formatPhoneNumber(c.phone_number)
                    : formatPhoneNumber(k);
    toast.info(`Starting a new conversation with ${label}`);
  };
  useEffect(() => {
    (async () => {
      try {
        const res = await adminAPI.getCompanies();
        const list = (res.data || []).filter(c => !(c.name || "").toLowerCase().includes("waleska"));
        list.sort((a, b) => (a.name?.toLowerCase().includes("avida") ? 0 : 1) - (b.name?.toLowerCase().includes("avida") ? 0 : 1));
        setCompanies(list);
        if (list.length && !fromNumber) setFromNumber(list[0].phone_number);
      } catch {
        try {
          const r = await twilioAPI.getAvailableNumbers();
          const mapped = (r.data || []).map(n => ({ id: n.company_id || n.phone_number, name: n.company_name || "My line", phone_number: n.phone_number }));
          setCompanies(mapped);
          if (mapped.length && !fromNumber) setFromNumber(mapped[0].phone_number);
        } catch { /* left empty */ }
      }
    })();
  }, []); // eslint-disable-line

  /* ─── Inbox data — calls, voicemails, sms threads ────────────── */
  const [recentCalls, setRecentCalls] = useState([]);
  const [voicemails, setVoicemails] = useState([]);
  const [smsThreads, setSmsThreads] = useState([]);
  const [loadingInbox, setLoadingInbox] = useState(true);
  const refreshInbox = useCallback(async () => {
    setLoadingInbox(true);
    const [rc, vm, sms] = await Promise.allSettled([
      callsAPI.getRecent(80),
      voicemailAPI.getAll({ limit: 80 }),
      smsAPI.getThreads(),
    ]);
    if (rc.status === "fulfilled")  setRecentCalls(rc.value.data || []);
    if (vm.status === "fulfilled")  setVoicemails(vm.value.data || []);
    if (sms.status === "fulfilled") setSmsThreads(sms.value.data || []);
    setLoadingInbox(false);
  }, []);
  useEffect(() => { refreshInbox(); }, [refreshInbox]);
  // Visibility-aware polling every 15s.
  useEffect(() => {
    const tick = () => { if (document.visibilityState === "visible") refreshInbox(); };
    const t = setInterval(tick, 15000);
    const onVis = () => { if (document.visibilityState === "visible") refreshInbox(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
  }, [refreshInbox]);

  /* ─── Queue + park + speed dials ────────────────────────────── */
  const [queueCallers, setQueueCallers] = useState([]);
  const [parkSlots, setParkSlots] = useState([]);
  const [speedDials, setSpeedDials] = useState([]);
  useEffect(() => {
    const load = async () => {
      const [q, p, sd] = await Promise.allSettled([
        adminAPI.getQueueStatus(),
        callsAPI.getParkedCalls(),
        speedDialAPI.getAll(),
      ]);
      if (q.status === "fulfilled") setQueueCallers(q.value.data?.calls || []);
      if (p.status === "fulfilled") setParkSlots(p.value.data?.slots || p.value.data || []);
      if (sd.status === "fulfilled") setSpeedDials(sd.value.data || []);
    };
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  /* ─── Contacts spine (for name enrichment + right-rail lookup) ── */
  const [contactsIndex, setContactsIndex] = useState({});
  useEffect(() => {
    (async () => {
      try {
        const r = await contactsAPI.getAll();
        const idx = {};
        for (const c of (r.data || [])) {
          const key = last10(c.phone_number);
          if (key) idx[key] = c;
        }
        setContactsIndex(idx);
      } catch { /* empty */ }
    })();
  }, []);

  /* ─── Junk/unnamed hide — per-session, PART 8 forbids storage ── *
   * Reload wipes the set. Called out in the report. */
  const [hiddenKeys, setHiddenKeys] = useState(new Set());
  const hide = useCallback((k) => setHiddenKeys(s => { const n = new Set(s); n.add(k); return n; }), []);
  const restore = useCallback((k) => setHiddenKeys(s => { const n = new Set(s); n.delete(k); return n; }), []); // eslint-disable-line

  /* ─── Rollup + counts ───────────────────────────────────────── *
   * Iter 484 — the inbox is TENANT-SCOPED to the currently-picked
   * outbound line. A super_admin who has multiple lines in the
   * header dropdown gets one clean tenant view at a time (no more
   * "Bylyngo cards next to Avida cards"). To see everything, they
   * flip the header pill to "All companies" (see CommsHeader.jsx)
   * which sets `?scope=all` and unlocks the filter here.
   */
  const scopeAll = params.get("scope") === "all";
  const activeCompany = companies.find(c => c.phone_number === fromNumber);
  const activeCompanyId = scopeAll ? null : (activeCompany?.id || null);
  const { inboxItems, counts } = useInboxRollup({
    recentCalls, voicemails, smsThreads, contactsIndex, hiddenKeys, activeCompanyId,
  });

  // Surface live counts to an embedding shell (Ring Center top-bar badges).
  useEffect(() => {
    if (onCounts) onCounts(counts);
  }, [counts, onCounts]);

  /* ─── Tab-title & audible alert when callers are waiting ─────── */
  const [origTitle] = useState(() => typeof document !== "undefined" ? document.title : "");
  useEffect(() => {
    if (typeof document === "undefined") return;
    const n = queueCallers.length;
    document.title = n > 0 ? `(${n}) callers waiting — Avida` : origTitle;
    // Audible chime only when the queue actually GREW (avoid looping)
    if (n > 0 && n > (Communications2._lastQ || 0)) {
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (AC) {
          const ctx = new AC();
          const o = ctx.createOscillator();
          const g = ctx.createGain();
          o.type = "sine"; o.frequency.value = 660;
          g.gain.value = 0.06;
          o.connect(g); g.connect(ctx.destination);
          o.start(); setTimeout(() => { o.stop(); ctx.close(); }, 220);
        }
      } catch { /* audio blocked */ }
    }
    Communications2._lastQ = n;
    return () => { document.title = origTitle; };
  }, [queueCallers.length, origTitle]);

  /* ─── "/" focuses the dial input (PART 8) ─────────────────── */
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      const typing = t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable);
      if (typing) return;
      if (e.key === "/") {
        e.preventDefault();
        const el = document.querySelector('[data-testid="comms2-dial-search"]');
        el && el.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /* ─── Row actions ───────────────────────────────────────────── */
  const doCall = (entry) => {
    call.makeCall && call.makeCall(e164(entry.phone), fromNumber);
  };
  const doText = (entry) => {
    setParamsBulk({ tab: "texts", who: entry.key });
  };
  const doPlayVm = (entry) => {
    // The overlay's audio player is authoritative — deep-link into the
    // voicemail page pre-selected so playback is a single click away.
    const vm = entry.voicemails.find(v => !v.is_read) || entry.voicemails[0];
    if (vm) navigate(`/voicemail?id=${vm.id}`);
  };
  const doHandled = async (entry) => {
    const promises = [];
    if (entry.smsThread && (entry.smsThread.unread_count || 0) > 0) {
      promises.push(smsAPI.markThreadAsRead(entry.smsThread.phone_number || entry.phone, entry.smsThread.our_number || entry.ourNumber));
    }
    for (const v of entry.voicemails) if (!v.is_read) promises.push(voicemailAPI.markRead(v.id));
    if (entry.needsCallback) {
      for (const c of entry.calls) {
        const isMissed = ["no-answer", "missed", "busy", "failed", "canceled"].includes(c.status);
        if (isMissed && c.direction === "inbound" && !c.handled) {
          promises.push(callsAPI.markHandled(c.id || c.call_sid, true));
        }
      }
    }
    if (!promises.length) return;
    const results = await Promise.allSettled(promises);
    const failed = results.filter(r => r.status === "rejected").length;
    if (failed) toast.error(`${failed} update${failed > 1 ? "s" : ""} failed`);
    else toast.success("Marked handled");
    refreshInbox();
  };
  const doContactCall = (c) => call.makeCall && call.makeCall(e164(c.phone_number), fromNumber);
  const doContactText = (c) => {
    const k = last10(c.phone_number);
    if (!k) return toast.error("This contact has no phone number.");
    // Iter 489 — check if the contact has any comms activity (call/vm/sms)
    // in the rollup. If yes, open the Timeline for that thread. If no,
    // seed the Text tab with a fresh thread for this phone so the user
    // can compose a first message. NOTE: all three URL fields have to
    // flip atomically because react-router doesn't chain functional
    // updates across sequential setParams() calls (see setParamsBulk).
    const hasActivity = inboxItems.some(e => (e.key || "").split("::")[0] === k);
    setParamsBulk({ tab: "texts", who: k });
    if (!hasActivity) {
      toast.info(`Starting a new conversation with ${c.first_name || ""} ${c.last_name || ""}`.trim() || "Starting a new conversation");
    }
  };

  // Iter 484 — delete an entire SMS thread. Backend enforces tenant
  // scoping (see routers/sms.py); the confirm dialog lives in
  // InboxCard so we just do the call + refresh.
  const doDelete = async (entry) => {
    const th = entry.smsThread;
    if (!th) return;
    const our = th.our_number || entry.ourNumber || fromNumber;
    const other = th.phone_number || entry.phone;
    if (!our || !other) {
      toast.error("Cannot delete: missing phone numbers on this thread.");
      return;
    }
    try {
      await smsAPI.deleteThread(other, our);
      toast.success("Text thread deleted");
      refreshInbox();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Delete failed");
    }
  };

  return (
    <div
      className={cn(
        "flex flex-col font-[Montserrat] overflow-hidden",
        embedded ? "h-full bg-white" : "h-[calc(100vh-60px)] bg-slate-50"
      )}
      data-testid="comms2-root"
    >
      {!embedded && (
        <CommsHeader
          companies={companies}
          fromNumber={fromNumber}
          setFromNumber={setFromNumber}
        />
      )}

      {/* Tab row — horizontally scrolls on narrow screens so all tabs
          stay reachable (fixes "Fax" being clipped on mobile).
          Hidden in embedded (Ring Center) mode — the shell's slim top
          bar drives the ?tab= param instead. */}
      {!embedded && (
      <div
        className="shrink-0 flex items-end justify-between px-2 sm:px-4 border-b bg-white gap-2"
        style={{ borderColor: NAVY_HAIRLINE }}
      >
        <div className="flex overflow-x-auto no-scrollbar -mb-px" data-testid="comms2-tab-row">
          {TABS.map(t => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className="h-12 px-4 sm:px-5 text-[14px] font-semibold border-b-2 -mb-[1px] transition-colors whitespace-nowrap shrink-0"
              style={activeTab === t.k
                ? { color: NAVY, borderColor: NAVY }
                : { color: "#64748b", borderColor: "transparent" }}
              data-testid={`comms2-tab-${t.k}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div
          className="hidden sm:flex items-center gap-2 pb-2 text-[13px] font-medium shrink-0"
          style={{ color: NAVY }}
          data-testid="comms2-needs-response"
        >
          Needs a response
          <span
            className="min-w-[26px] h-6 rounded-md flex items-center justify-center text-[13px] font-semibold tabular-nums px-2"
            style={{ background: counts.needsResponse > 0 ? GOLD : "rgba(20,56,117,0.08)", color: NAVY }}
          >
            {counts.needsResponse}
          </span>
        </div>
      </div>
      )}

      {/* Body — left rail + tab content */}
      <div className={cn("flex-1 flex overflow-hidden gap-2 sm:gap-3", embedded ? "p-0" : "p-2 sm:p-3")}>
        {!embedded && (
        <LeftRail
          counts={counts}
          queueCallers={queueCallers}
          parkSlots={parkSlots}
          speedDials={speedDials}
          contactsIndex={contactsIndex}
          fromNumber={fromNumber}
          onFilter={(f) => {
            // Iter 508j — LeftRail's counter chips still emit legacy
            // filter names (`needs / calls / texts / vm / all`). Map
            // them onto the new 6-tab layout: `needs`/`vm`/`all` all
            // land on Inbox (with the appropriate filter chip),
            // `calls` and `texts` still get their own dedicated tab.
            const RAIL_TO_TAB = {
              calls: { tab: "calls" },
              texts: { tab: "texts" },
              needs: { tab: "inbox", filter: "needs" },
              vm:    { tab: "inbox", filter: "vm" },
              all:   { tab: "inbox", filter: "all" },
            };
            const target = RAIL_TO_TAB[f] || { tab: "inbox", filter: "needs" };
            setParams(p => {
              const n = new URLSearchParams(p);
              n.set("tab", target.tab);
              if (target.filter) n.set("filter", target.filter);
              else n.delete("filter");
              return n;
            }, { replace: true });
          }}
          onPickupPark={async (slot) => {
            try { await callsAPI.pickupParkedCall(slot); toast.success(`Picking up park ${slot}`); }
            catch (e) { toast.error(toErrorMessage(e, "Failed to pick up")); }
          }}
        />
        )}

        <div className={cn("flex-1 flex bg-white overflow-hidden", embedded ? "" : "rounded-xl border shadow-sm")} style={embedded ? undefined : { borderColor: NAVY_HAIRLINE }}>
          {/* Iter 506+ · unified inbox surface — one InboxTab instance
              renders both the Inbox work-queue tab (filter chips visible)
              and the pure Texts tab (filter LOCKED to 'texts', no chips).
              Iter 508j — when a tab's lockedFilter is null the tab is
              treated as the primary Inbox surface: the URL `filter`
              param drives which subset is shown, and the FilterChips
              row inside InboxTab remains visible so the user can flip
              between Needs / Missed / VM / All without leaving the tab. */}
          {(() => {
            const tabDef = TABS.find(t => t.k === activeTab);
            if (!tabDef || tabDef.mode !== "inbox") return null;
            const isLocked = tabDef.lockedFilter != null;
            const activeFilter = isLocked ? tabDef.lockedFilter : filter;
            return (
              <InboxTab
                inboxItems={inboxItems}
                loading={loadingInbox}
                filter={activeFilter}
                setFilter={isLocked ? () => {} : setFilter}
                lockFilter={isLocked}
                openEntryKey={openKey}
                setOpenEntryKey={setOpenKey}
                onOpenTimeline={() => {}}
                onCall={doCall}
                onText={doText}
                onPlayVm={doPlayVm}
                onMarkHandled={doHandled}
                onHide={hide}
                onDelete={doDelete}
                onNewText={openNewText}
                navigate={navigate}
                fromNumber={fromNumber}
                onInboxChanged={refreshInbox}
                shellMode={embedded}
                autoOpenFirst={embedded}
              />
            );
          })()}
          {activeTab === "calls" && (
            <CallLogTab activeCompanyId={activeCompanyId} />
          )}
          {/* Iter 508j · Dialer tab retired — the dial pad in the left
              rail is present on every surface, so a dedicated tab was
              redundant. Legacy `?tab=dialer` links are redirected to
              Inbox by LEGACY_TAB_REDIRECT above. */}
          {activeTab === "contacts"   && <ContactsTab onCall={doContactCall} onText={doContactText} navigate={navigate} />}
          {activeTab === "directory"  && (
            <div className="flex-1 overflow-y-auto p-5">
              <DirectoryTab call={call} fromNumber={fromNumber} isAdmin={isAdmin} />
            </div>
          )}
          {activeTab === "text-blast" && <TextBlastTab />}
          {activeTab === "fax"        && <FaxTab />}
        </div>
      </div>

      {/* New-text launcher — wired to the gold "New text" button in
          both the Inbox and Texts tabs. Pick a saved contact or
          type a raw number; the modal hands off to the URL router
          which opens the SMS timeline for that thread. */}
      <NewTextComposerModal
        open={newTextOpen}
        onClose={closeNewText}
        onPick={handleNewTextPick} />
    </div>
  );
}
Communications2._lastQ = 0;
